import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("sr_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      const path = window.location.pathname;
      if (path !== "/login" && path !== "/register" && path !== "/") {
        localStorage.removeItem("sr_token");
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

/**
 * withRetry: re-runs `fn` up to `retries` times when the failure looks like a
 * transient infrastructure blip (Cloudflare 5xx, network drop, timeout). Used
 * for video upload + MP4 export which the unstable production pod intermittently
 * returns 520/503 on. Does NOT retry on real client/server errors (4xx, 5xx with
 * a JSON body) since those mean "you're doing something wrong" and a retry won't
 * help.
 */
export const withRetry = async (fn, { retries = 2, onRetry = null, baseDelayMs = 1500 } = {}) => {
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fn(attempt);
    } catch (err) {
      lastErr = err;
      const status = err?.response?.status;
      const code = err?.code;
      const transient =
        // No response at all (network drop / connection killed)
        !err?.response ||
        // Cloudflare/proxy errors
        status === 502 || status === 503 || status === 504 || status === 520 ||
        status === 521 || status === 522 || status === 523 || status === 524 ||
        // Axios timeout
        code === "ECONNABORTED" ||
        // Fetch network failure
        (err?.message || "").toLowerCase().includes("network");
      if (!transient || attempt === retries) {
        throw err;
      }
      if (onRetry) onRetry(attempt + 1, retries + 1);
      await new Promise((res) => setTimeout(res, baseDelayMs * (attempt + 1)));
    }
  }
  throw lastErr;
};
